import React, { useState } from "react";
import { Avatar, Form, Image, Input, Select, Switch, Upload } from "antd";
import PhoneNumbersField from "../../commonComponents/PhoneNumbersField";

const ProfileDrawerContent = ({
  profileForm,
  user,
  profilePicList,
  userInitials,
  languages,
  languagesLoading,
  phoneNumbers,
  onUpdatePhone,
  onAddPhone,
  onRemovePhone,
  beforeProfilePicUpload,
  onProfilePicRemove,
  onLoadLanguages,
  skipArchiveRestoreConfirm,
  onToggleSkipArchive,
}) => {
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  // Preview inside an antd Image modal instead of opening a new tab
  // (data-URL/blob previews get blocked → about:blank#blocked).
  const handlePreview = file => {
    const src = file?.url || file?.thumbUrl;
    if (!src) return;
    setPreviewImage(src);
    setPreviewOpen(true);
  };

  return (
    <Form form={profileForm} layout="vertical">
      <p className="text-black-88 font-semibold text-base mb-4">Personal information</p>

      {/* Profile picture uploader */}
      <Form.Item label={<span className="form-field-label">Profile picture</span>}>
        <div className="profilePicUpload bg-[#D1D5DB] rounded-full inline-block">
          <Upload
            name="profilePic"
            listType="picture-circle"
            accept="image/*"
            fileList={profilePicList}
            beforeUpload={beforeProfilePicUpload}
            onRemove={onProfilePicRemove}
            onPreview={handlePreview}
            maxCount={1}
            showUploadList={{
              showPreviewIcon: true,
              showRemoveIcon: false,
              showDownloadIcon: false,
            }}
          >
            {profilePicList.length < 1 && (
              <Avatar size={70} className="cursor-pointer">
                {userInitials}
              </Avatar>
            )}
          </Upload>
        </div>
        {previewImage && (
          <Image
            wrapperStyle={{ display: "none" }}
            preview={{
              visible: previewOpen,
              onVisibleChange: visible => setPreviewOpen(visible),
              afterOpenChange: visible => !visible && setPreviewImage(""),
            }}
            src={previewImage}
          />
        )}
      </Form.Item>

      <Form.Item
        name="firstName"
        label={<span className="form-field-label">First name</span>}
        rules={[{ required: true, message: "Please enter first name" }]}
      >
        <Input placeholder="Enter first name" />
      </Form.Item>

      <Form.Item
        name="lastName"
        label={<span className="form-field-label">Last name</span>}
        rules={[{ required: true, message: "Please enter last name" }]}
      >
        <Input placeholder="Enter last name" />
      </Form.Item>

      <Form.Item
        name="preferredName"
        label={<span className="form-field-label">Preferred name</span>}
      >
        <Input placeholder="Enter preferred name" />
      </Form.Item>

      {/* Phone Numbers Section */}
      <div className="mb-4">
        <span className="form-field-label block mb-2">Phone(s)</span>
        <PhoneNumbersField
          phoneNumbers={phoneNumbers}
          onUpdate={onUpdatePhone}
          onAdd={onAddPhone}
          onRemove={onRemovePhone}
        />
      </div>

      <Form.Item
        name="email"
        label={<span className="form-field-label">Email address</span>}
        rules={[{ required: true, type: "email", message: "Please enter a valid email" }]}
      >
        <Input placeholder="Enter email address" />
      </Form.Item>

      <p className="text-black-88 font-semibold text-base mt-10 mb-4">Other information</p>

      <Form.Item
        name="preferredLanguage"
        label={<span className="form-field-label">Preferred language</span>}
      >
        <Select
          placeholder="Select preferred language"
          loading={languagesLoading}
          onDropdownVisibleChange={open => {
            if (open) onLoadLanguages();
          }}
        >
          {(languages || []).map(item => (
            <Select.Option key={item?.id} value={item?.languageCode}>
              {item.languageName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {/* Do not translate (server-side preference, read on submit) */}
      <div className="flex items-center mt-5 gap-2">
        <Form.Item name="isTranslationEnabled" valuePropName="checked" noStyle>
          <Switch size="small" />
        </Form.Item>
        <span className="form-field-label">Do not translate</span>
      </div>

      {/* Skip archive & restore confirmation (browser-only preference, saved immediately) */}
      <div className="mt-4">
        <div className="flex items-center gap-2">
          <Switch size="small" checked={skipArchiveRestoreConfirm} onChange={onToggleSkipArchive} />
          <span className="form-field-label">Skip archive &amp; restore confirmation</span>
        </div>
        <p className="text-xs text-black/[0.45] mt-1 mb-0 ml-9">
          When enabled, <strong>archiving</strong> or <strong>restoring</strong> a lead will happen
          immediately without showing the confirmation dialog. This preference is saved to this
          browser only.
        </p>
      </div>
    </Form>
  );
};

export default ProfileDrawerContent;
